import type { ReactNode } from "react";
import { QueryClientProvider } from "@tanstack/react-query";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { ConnectivityMonitor } from "@app/components/connectivity-monitor";
import { OfflineBanner } from "@app/components/offline-banner";
import { getQueryClient } from "@lib/query/client";
import { createQueryCachePersister } from "@lib/query/persister";
import { env } from "@config/env";

const PERSIST_MAX_AGE = 1000 * 60 * 60 * 24;

export function QueryProvider({ children }: { children: ReactNode }) {
  const persister =
    typeof window !== "undefined" ? createQueryCachePersister() : null;
  const queryClient = getQueryClient();

  const content = (
    <>
      <ConnectivityMonitor />
      <OfflineBanner />
      {children}
      {env.enableQueryDevtools() && (
        <ReactQueryDevtools initialIsOpen={false} />
      )}
    </>
  );

  if (persister) {
    return (
      <PersistQueryClientProvider
        client={queryClient}
        persistOptions={{ persister, maxAge: PERSIST_MAX_AGE }}
      >
        {content}
      </PersistQueryClientProvider>
    );
  }

  return <QueryClientProvider client={queryClient}>{content}</QueryClientProvider>;
}
